import {
	bindEventListeners,
	BREAKPOINTS,
	isDesktop,
	isHeroFilterVisible,
	isMobile,
	throttle,
} from '../utility/index.js';

/**
 * Header component managing responsive behavior, modals, and interactions
 * Handles interstitial overlays, mobile CTAs, hero filter visibility, and sub-menus
 *
 * Trigger Behavior:
 * - Triggers are disabled when inside an element with data-logo-banner-interstitial="false"
 * - This allows for selective disabling of header functionality in specific contexts
 */
class Header {
	static DESKTOP_BREAKPOINT = BREAKPOINTS.HEADER_DESKTOP;
	static SELECTORS = {
		FOOTER: '[data-footer]',
		HEADER: '[data-header]',
		HERO: '[data-search-hero]',
		INTERSTITIAL: '[data-header-interstitial]',
		INTERSTITIAL_TRIGGERS:
			'[data-header-interstitial-trigger]:not([data-header-interstitial-trigger="False"])',
		MOBILE_CTA_CLOSE: '[data-sticky-mobile-cta-close]',
		MOBILE_CTA_TRIGGERS:
			'[data-sticky-mobile-cta-trigger]:not([data-sticky-mobile-cta-trigger="False"])',
		MOBILE_NAV_ITEMS: '.header__nav--mobile .header__nav-items',
		MOBILE_STICKY_CTA: '[data-sticky-mobile-cta]',
		MOBILE_SUB_MENU_TOGGLE: '[data-mobile-sub-menu-toggle]',
		MOBILE_SUB_MENUS: '[data-mobile-sub-menu]',
		MULTI_LEVEL_DROPDOWN: '.header__dropdown--multi-level',
		SEARCH_FILTER_FORM: '[data-search-filter-form]',
		// Elements that should not close mobile CTA when clicked
		DROPDOWN_MODAL: '[data-search-filter-form-dropdown-modal]',
		DROPDOWN_OPTION: '[data-search-filter-form-option]',
		DROPDOWN_TRIGGER: '[data-search-filter-form-modal-trigger]',
		DROPDOWN_CLOSE_BUTTON: '[data-search-filter-form-close-button]',
		DROPDOWN_DESELECT_BUTTON: '[data-search-filter-form-deselect-button]',
		DROPDOWN_SEARCH_INPUT: '[data-search-filter-form-search-input]',
		DROPDOWN_TABBED_OPTION: '[data-search-filter-form-tabbed-option]',
		DROPDOWN_SINGLE_SELECT: '[data-search-filter-form-single-select]',
	};
	static CLASSES = {
		ANIMATIONS_ENABLED: 'header--animations-enabled',
		BODY_INTERSTITIAL_ACTIVE: 'body--interstitial-active',
		DISPLAY_FILTERS: 'header--display-filters',
		FOOTER_MOBILE_CTA_OFFSET: 'gt-footer--mobile-cta-offset',
		INTERSTITIAL_ACTIVE: 'header--interstitial-active',
		MOBILE_CTA_ACTIVE: 'sticky-mobile-cta--active',
		MOBILE_CTA_DISPLAY: 'sticky-mobile-cta--display',
		SUBMENU_ACTIVE: 'active',
	};

	constructor() {
		this.headerContainer = document.querySelector(Header.SELECTORS.HEADER);
		this.header = this.headerContainer?.closest('header');

		// Early exit if critical elements are missing
		if (!this.header || !this.headerContainer) {
			// eslint-disable-next-line no-console
			console.warn('Header: Critical elements not found');
			return;
		}

		this.mobileStickyCta = document.querySelector(
			Header.SELECTORS.MOBILE_STICKY_CTA,
		);
		this.hero = document.querySelector(Header.SELECTORS.HERO);
		this.footer = document.querySelector(Header.SELECTORS.FOOTER);
		this.animationsEnabled = false;

		this.init();
	}

	init() {
		this.initHeaderInterstitial();
		this.initHeroFilterObserver();
		this.initMobileStickyCta();
		this.initMobileSubMenus();
		this.initMultiLevelDropdown();
		this.initResponsiveHandlers();
	}

	/**
	 * Sets up desktop-only overlay that can be triggered by specific elements
	 */
	initHeaderInterstitial() {
		this.headerInterstitial = this.headerContainer?.querySelector(
			Header.SELECTORS.INTERSTITIAL,
		);
		if (!this.headerInterstitial) {
			return;
		}

		this.interstitialTriggers = document.querySelectorAll(
			Header.SELECTORS.INTERSTITIAL_TRIGGERS,
		);
		this.isInterstitialActive = false;

		this.boundOpenInterstitial = this.openInterstitial.bind(this);
		this.boundCloseInterstitial = this.closeInterstitial.bind(this);

		bindEventListeners(this.interstitialTriggers, 'click', (e) => {
			e.stopPropagation();

			// Check if logo banner triggers are disabled
			if (this.isLogoTriggerDisabled(e.target)) {
				return;
			}

			// Check that the header contains the search filter form
			if (!this.header?.querySelector(Header.SELECTORS.SEARCH_FILTER_FORM)) {
				return;
			}

			if (isDesktop(Header.DESKTOP_BREAKPOINT)) {
				e.preventDefault();
				this.boundOpenInterstitial();
			}
		});

		this.addGlobalEventListener(
			'click',
			this.handleInterstitialClickOutside.bind(this),
			{ passive: true },
		);
		this.addGlobalEventListener(
			'keydown',
			this.handleInterstitialEscape.bind(this),
			{ passive: true },
		);
	}

	/**
	 * Uses IntersectionObserver to track when hero filter scrolls out of view
	 * Accounts for header height to determine accurate intersection with hero filter
	 * Manages header state changes based on filter visibility for both desktop and mobile
	 */
	initHeroFilterObserver() {
		if (!this.hero) return;

		this.heroFilter = this.hero.querySelector(
			Header.SELECTORS.SEARCH_FILTER_FORM,
		);
		if (!this.heroFilter || !this.headerContainer) return;

		this.boundSetFiltersDisplay = this.setFiltersDisplay.bind(this);
		this.boundSetAnimationsEnabled = this.setAnimationsEnabled.bind(this);
		this.boundSetMobileCtaDisplay = this.setMobileCtaDisplay.bind(this);

		// Calculate header height for accurate intersection detection
		this.headerHeight = this.header.offsetHeight;

		this.heroFilterObserver = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				const isVisible = entry.intersectionRatio >= 0.5;
				this.handleHeroFilterVisibility(isVisible);
			},
			{
				root: null,
				threshold: [0, 0.5, 1],
				rootMargin: `-${this.headerHeight}px 0px 0px 0px`, // Account for header height
			},
		);

		this.heroFilterObserver.observe(this.heroFilter);
	}

	/**
	 * Sets up mobile-only sticky call-to-action that appears when hero filter is not visible
	 */
	initMobileStickyCta() {
		if (!this.mobileStickyCta) return;

		this.mobileStickyCtaTriggers = document.querySelectorAll(
			Header.SELECTORS.MOBILE_CTA_TRIGGERS,
		);
		this.mobileStickyCtaClose = this.mobileStickyCta.querySelector(
			Header.SELECTORS.MOBILE_CTA_CLOSE,
		);

		if (this.mobileStickyCtaTriggers.length === 0 || !this.mobileStickyCtaClose)
			return;

		this.isMobileCtaActive = false;
		this.boundOpenMobileCta = this.openMobileCta.bind(this);
		this.boundCloseMobileCta = this.closeMobileCta.bind(this);
		this.mobileCtaTriggerHandler = this.handleMobileCtaTriggerClick.bind(this);

		this.bindMobileCtaTriggers();
		this.mobileStickyCtaClose.addEventListener(
			'click',
			this.boundCloseMobileCta,
		);
		this.addGlobalEventListener(
			'click',
			this.handleMobileCtaClickOutside.bind(this),
			{ passive: true },
		);

		document.addEventListener(
			'type-of-care-mobile-click',
			this.openMobileCta.bind(this),
		);
	}

	/**
	 * Sets up toggle button and mutation observer to watch for sub-menu state changes
	 * Manages visibility of the close-all toggle based on active sub-menus
	 * Controls nav-items overflow to prevent scroll issues when sub-menus are active
	 */
	initMobileSubMenus() {
		this.mobileSubMenuToggle = this.header.querySelector(
			Header.SELECTORS.MOBILE_SUB_MENU_TOGGLE,
		);
		this.mobileSubMenus = this.header.querySelectorAll(
			Header.SELECTORS.MOBILE_SUB_MENUS,
		);
		this.mobileNavItem = this.header.querySelector(
			Header.SELECTORS.MOBILE_NAV_ITEMS,
		);

		if (!this.mobileSubMenuToggle || this.mobileSubMenus.length === 0) return;

		// Bind events
		this.boundCloseAllSubMenus = () => {
			this.mobileSubMenus.forEach((menu) =>
				menu.classList.remove(Header.CLASSES.SUBMENU_ACTIVE),
			);
		};
		this.mobileSubMenuToggle.addEventListener(
			'click',
			this.boundCloseAllSubMenus,
		);

		// Observe sub-menus for changes
		this.mutationObserver = new MutationObserver(
			throttle(() => {
				const hasActiveSubMenu = Array.from(this.mobileSubMenus).some(
					(menu) =>
						menu.classList.contains(Header.CLASSES.SUBMENU_ACTIVE) ||
						menu.querySelector(`.${Header.CLASSES.SUBMENU_ACTIVE}`),
				);

				this.updateMobileSubMenuState(hasActiveSubMenu);
			}, 50),
		);

		for (const menu of this.mobileSubMenus) {
			this.mutationObserver.observe(menu, {
				attributes: true,
				attributeFilter: ['class'],
				subtree: true,
			});
		}
	}

	/**
	 * Applies positioning fix to multi-level dropdown if present
	 */
	initMultiLevelDropdown() {
		const multiLevelDropdown = this.header?.querySelector(
			Header.SELECTORS.MULTI_LEVEL_DROPDOWN,
		);
		if (!multiLevelDropdown) return;

		multiLevelDropdown.style.setProperty('left', 'auto', 'important');
		multiLevelDropdown.style.setProperty('right', '1.12rem', 'important');
	}

	/**
	 * Manages screen size changes, closes active modals when switching between desktop/mobile
	 * Updates hero filter visibility and handles all responsive state changes
	 */
	initResponsiveHandlers() {
		this.handleResize = throttle(() => {
			const isDesktopView = isDesktop(Header.DESKTOP_BREAKPOINT);

			if (isDesktopView && this.isMobileCtaActive) {
				this.closeMobileCta();
			}

			if (!isDesktopView && this.isInterstitialActive) {
				this.boundCloseInterstitial();
			}

			this.updateHeroFilterVisibility();
		}, 100);

		this.addGlobalEventListener('resize', this.handleResize, {
			passive: true,
		});
	}

	/**
	 * Add global event listener
	 * @param {string}   event   - Event type
	 * @param {Function} handler - Event handler
	 * @param {Object}   options - Event listener options
	 */
	addGlobalEventListener(event, handler, options = {}) {
		document.addEventListener(event, handler, options);
	}

	/**
	 * Check if logo banner triggers are disabled by looking for a parent element
	 * with data-logo-banner-interstitial="false"
	 * @param {Element} triggerElement - The trigger element to check
	 * @return {boolean} - True if logo banner triggers are disabled
	 */
	isLogoTriggerDisabled(triggerElement) {
		return (
			triggerElement.closest('[data-logo-banner-interstitial="false"]') !== null
		);
	}

	/**
	 * Bind event listeners to all mobile CTA triggers
	 */
	bindMobileCtaTriggers() {
		// Remove existing listeners to prevent duplicates
		this.unbindMobileCtaTriggers();

		bindEventListeners(
			this.mobileStickyCtaTriggers,
			'click',
			this.mobileCtaTriggerHandler,
		);
	}

	/**
	 * Handle mobile CTA trigger click
	 * @param {Event} e - Click event
	 */
	handleMobileCtaTriggerClick(e) {
		// Check if we are not on mobile, or if logo banner triggers are disabled
		if (
			!isMobile(Header.DESKTOP_BREAKPOINT) ||
			this.isLogoTriggerDisabled(e.target)
		)
			return;

		// Check if mobile CTA should be prevented (e.g., when hero filters are visible)
		if (e.target.hasAttribute('data-prevent-mobile-cta')) return;

		// Check that the header contains the search filter form
		if (!this.header?.querySelector(Header.SELECTORS.SEARCH_FILTER_FORM))
			return;

		const isMainTrigger = this.mobileStickyCta.contains(e.target);
		if (!isMainTrigger) {
			e.stopPropagation();
			e.preventDefault();
			this.openMobileCta();
		} else {
			e.preventDefault();
			this.openMobileCtaFromUserClick();
		}
	}

	/**
	 * Check if click target is within dropdown elements that should not close mobile CTA
	 * @param {Element} target - The clicked element
	 * @return {boolean} - True if target is within dropdown elements
	 */
	isWithinDropdownElements(target) {
		const dropdownSelectors = [
			Header.SELECTORS.DROPDOWN_MODAL,
			Header.SELECTORS.DROPDOWN_OPTION,
			Header.SELECTORS.DROPDOWN_TRIGGER,
			Header.SELECTORS.DROPDOWN_CLOSE_BUTTON,
			Header.SELECTORS.DROPDOWN_DESELECT_BUTTON,
			Header.SELECTORS.DROPDOWN_SEARCH_INPUT,
			Header.SELECTORS.DROPDOWN_TABBED_OPTION,
			Header.SELECTORS.DROPDOWN_SINGLE_SELECT,
		];

		return dropdownSelectors.some(
			(selector) => target.closest(selector) !== null,
		);
	}

	/**
	 * Lock body scroll with iOS Safari compatibility
	 * Disables touch scrolling and sets overflow hidden
	 */
	lockBodyScroll() {
		document.body.style.overflow = 'hidden';
		document.body.style.touchAction = 'none';
	}

	/**
	 * Restore body scroll
	 */
	restoreBodyScroll() {
		document.body.style.overflow = '';
		document.body.style.touchAction = '';
	}

	/**
	 * Unbind existing mobile CTA trigger listeners
	 */
	unbindMobileCtaTriggers() {
		if (this.mobileCtaTriggerHandler) {
			this.mobileStickyCtaTriggers.forEach((trigger) => {
				trigger.removeEventListener('click', this.mobileCtaTriggerHandler);
			});
		}
	}

	/**
	 * Reinitialize mobile CTA triggers for dynamic content
	 * Call this when new triggers are added to the DOM
	 */
	reinitMobileCtaTriggers() {
		if (!this.mobileStickyCta) return;

		const newTriggers = document.querySelectorAll(
			Header.SELECTORS.MOBILE_CTA_TRIGGERS,
		);
		const hasChanged =
			newTriggers.length !== this.mobileStickyCtaTriggers.length ||
			Array.from(newTriggers).some(
				(trigger, index) =>
					!this.mobileStickyCtaTriggers[index] ||
					trigger !== this.mobileStickyCtaTriggers[index],
			);

		if (hasChanged) {
			this.mobileStickyCtaTriggers = newTriggers;
			this.bindMobileCtaTriggers();
		}
	}

	/**
	 * Updates mobile sub-menu state including toggle visibility and nav overflow
	 * @param {boolean} hasActiveSubMenu - Whether there are active sub-menus
	 */
	updateMobileSubMenuState(hasActiveSubMenu) {
		if (!isMobile(Header.DESKTOP_BREAKPOINT)) return;

		// Update toggle visibility
		this.mobileSubMenuToggle.style.display = hasActiveSubMenu ? 'flex' : 'none';

		if (!this.mobileNavItem) return;

		if (hasActiveSubMenu) {
			this.mobileNavItem.style.overflowY = 'hidden';
			this.mobileNavItem.scrollTop = 0;
		} else {
			this.mobileNavItem.style.overflowY = '';
		}
	}

	/**
	 * Public method to reinitialize mobile CTA triggers
	 * Can be called from other scripts when new triggers are added
	 */
	static reinitMobileCtaTriggers() {
		if (window.headerInstance) {
			window.headerInstance.reinitMobileCtaTriggers();
		}
	}

	/**
	 * Close the interstitial overlay
	 * Removes CSS classes and re-enables body scrolling
	 */
	closeInterstitial() {
		this.headerContainer.classList.remove(Header.CLASSES.INTERSTITIAL_ACTIVE);
		document.body.classList.remove(Header.CLASSES.BODY_INTERSTITIAL_ACTIVE);

		// If the hero filters are still visible, remove the display filters class
		if (isHeroFilterVisible(this.heroFilter, this.headerHeight)) {
			this.headerContainer.classList.remove(Header.CLASSES.DISPLAY_FILTERS);
		}

		this.isInterstitialActive = false;
	}

	/**
	 * Open the interstitial overlay and disables body scrolling
	 */
	openInterstitial() {
		this.headerContainer.classList.add(
			Header.CLASSES.INTERSTITIAL_ACTIVE,
			Header.CLASSES.DISPLAY_FILTERS,
		);
		document.body.classList.add(Header.CLASSES.BODY_INTERSTITIAL_ACTIVE);
		this.isInterstitialActive = true;
	}

	/**
	 * Handle click outside interstitial overlay
	 * @param {Event} e - Click event
	 */
	handleInterstitialClickOutside(e) {
		if (!this.isInterstitialActive || this.headerContainer.contains(e.target))
			return;
		this.boundCloseInterstitial();
	}

	/**
	 * Handle escape key for interstitial overlay
	 * @param {Event} e - Keyboard event
	 */
	handleInterstitialEscape(e) {
		if (!this.isInterstitialActive || e.key !== 'Escape') return;
		this.boundCloseInterstitial();
	}

	/**
	 * Enable header animations
	 */
	setAnimationsEnabled() {
		this.headerContainer.classList.add(Header.CLASSES.ANIMATIONS_ENABLED);
	}

	/**
	 * Set header filters display state
	 * @param {boolean} isActive - Whether filters should be displayed
	 */
	setFiltersDisplay(isActive) {
		this.headerContainer.classList.toggle(
			Header.CLASSES.DISPLAY_FILTERS,
			Boolean(isActive),
		);
	}

	/**
	 * Handle hero filter visibility changes for both desktop and mobile
	 * @param {boolean} isVisible - Whether the hero filter is at least 50% visible
	 */
	handleHeroFilterVisibility(isVisible) {
		const isDesktopView = isDesktop(Header.DESKTOP_BREAKPOINT);

		if (isDesktopView) {
			// Add animations enabled class on the first time the hero filter is visible
			if (!isVisible && !this.animationsEnabled) {
				this.boundSetAnimationsEnabled();
				this.animationsEnabled = true;
			}

			// Add class when hero filter is NOT visible; remove when visible again
			this.boundSetFiltersDisplay(!isVisible);
		} else {
			// Add mobile animations enabled class when hero filter is NOT visible
			this.boundSetMobileCtaDisplay(!isVisible);
		}
	}

	/**
	 * Update hero filter visibility based on current viewport
	 * Uses 50% visibility threshold for consistency with IntersectionObserver
	 */
	updateHeroFilterVisibility() {
		if (this.heroFilterObserver && this.heroFilter) {
			const isVisible = isHeroFilterVisible(this.heroFilter, this.headerHeight);
			this.handleHeroFilterVisibility(isVisible);
		}
	}

	/**
	 * Close the mobile sticky CTA
	 */
	closeMobileCta() {
		this.mobileStickyCta.classList.remove(Header.CLASSES.MOBILE_CTA_ACTIVE);
		this.restoreBodyScroll();
		this.isMobileCtaActive = false;
	}

	/**
	 * Open the mobile sticky CTA from user click (with tracking)
	 */
	openMobileCtaFromUserClick() {
		this.openMobileCta();
		this.trackStickyMobileCtaOpen();
	}

	/**
	 * Open the mobile sticky CTA
	 */
	openMobileCta() {
		// Only update if not already active
		if (!this.isMobileCtaActive) {
			this.setMobileCtaDisplay(true);
			this.mobileStickyCta.classList.add(Header.CLASSES.MOBILE_CTA_ACTIVE);
			this.lockBodyScroll();
			this.isMobileCtaActive = true;
		}
	}

	/**
	 * Handle click outside mobile CTA
	 * @param {Event} e - Click event
	 */
	handleMobileCtaClickOutside(e) {
		if (
			!this.isMobileCtaActive ||
			this.mobileStickyCta.contains(e.target) ||
			this.isWithinDropdownElements(e.target)
		)
			return;

		this.boundCloseMobileCta();
	}

	/**
	 * Set mobile CTA display state
	 * @param {boolean} isEnabled - Whether mobile CTA should be displayed
	 */
	setMobileCtaDisplay(isEnabled) {
		if (this.mobileStickyCta) {
			this.mobileStickyCta.classList.toggle(
				Header.CLASSES.MOBILE_CTA_DISPLAY,
				Boolean(isEnabled),
			);
		}

		if (this.footer) {
			this.footer.classList.toggle(
				Header.CLASSES.FOOTER_MOBILE_CTA_OFFSET,
				Boolean(isEnabled),
			);
		}
	}

	/**
	 * Track sticky mobile CTA open event
	 */
	trackStickyMobileCtaOpen() {
		if (!window.SegmentTracking?.trackButtonClick) {
			// eslint-disable-next-line no-console
			console.warn('SegmentTracking not available for mobile CTA tracking');
			return;
		}

		try {
			window.SegmentTracking.trackButtonClick('Sticky CTA Open - Mobile');
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error tracking sticky mobile CTA open:', error);
		}
	}
}

document.addEventListener('DOMContentLoaded', () => {
	window.headerInstance = new Header();
});
